import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { AnimatePresence, motion } from 'framer-motion'
import FocusLock from 'react-focus-lock'
import { toast } from 'sonner'
import { X, Sparkles, Check } from 'lucide-react'
import * as Sentry from '@sentry/react'
import {
  FREE_CHARS_PER_MONTH,
  FREE_CHARS_PER_TRANSLATION,
  GUEST_CHARS_PER_MONTH,
} from '@/lib/baba-limits'
import { useSendMagicLinkMutation, useVerifyLoginCodeMutation } from '../store/babaApi'
import { getAuthEmail, getAuthToken, isSignedIn, setAuthToken } from '@/lib/baba-auth'
import { handAccountLocalStateTo } from '@/lib/accountLocalState'
import { registrationEventId, trackMagicLinkRequested, trackSignUp, trackLogin } from '@/lib/analytics'
import { getStoredReferralCode } from '@/lib/referral'
import { broadcastSubscriptionStatus, seedSubscriptionFromAuth } from '@/lib/baba-api'
import { startGoogleSignIn } from '@/lib/googleSignIn'
import { OPEN_SIGN_IN_EVENT, type OpenSignInEventDetail } from '@/lib/signInEvents'
import { markPhoneLinkPromptPending } from '@/lib/phoneLink'

const HEARTBEAT_SUBSCRIBE_URL = 'https://www.itsbaba.com/api/blog-subscribe'

function enrollHeartbeat(email: string) {
  void fetch(HEARTBEAT_SUBSCRIBE_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, source: 'web-signup', segment: 'web-signup' }),
  }).catch(() => {})
}

export type SignInIntent =
  | 'sidebar'
  | 'library'
  | 'account'
  | 'settings'
  | 'upgrade-free'

interface SignInContextValue {
  open: (intent?: SignInIntent) => void
  close: () => void
}

const SignInContext = createContext<SignInContextValue | null>(null)

export function useSignIn(): SignInContextValue {
  const ctx = useContext(SignInContext)
  if (!ctx) return { open: () => {}, close: () => {} }
  return ctx
}

const freeChars = FREE_CHARS_PER_MONTH.toLocaleString()
const guestChars = GUEST_CHARS_PER_MONTH.toLocaleString()

const CONFIGS: Record<SignInIntent, { title: string; subtitle: string; perks: string[] }> = {
  sidebar: {
    title: 'Sign In to baba, Free',
    subtitle: 'No password, no card. Just an email.',
    perks: [
      `${freeChars} characters/month (up from ${guestChars})`,
      `${FREE_CHARS_PER_TRANSLATION.toLocaleString()} characters per translation`,
      'Save translations to your library',
      'Your last 12 translations stay handy',
    ],
  },
  'upgrade-free': {
    title: `Get ${freeChars} Free Characters Every Month`,
    subtitle: 'Free account. No card, no password. We email you a sign-in link.',
    perks: [
      `${freeChars} characters/month`,
      `${FREE_CHARS_PER_TRANSLATION.toLocaleString()} characters per translation`,
      'Save translations to your library',
    ],
  },
  library: {
    title: 'Sign In to Use Your Library',
    subtitle: 'Saved phrases and recent translations, all in one place.',
    perks: [
      'Saved phrases in one place',
      'Your recent translations, organized',
      `${freeChars} characters/month`,
      'No password required',
    ],
  },
  account: {
    title: 'Sign In to Your Account',
    subtitle: 'Free account with an email link. No password.',
    perks: [
      'Manage your plan',
      'Cloud-synced history & favorites',
      'Works across web, extension & mobile',
    ],
  },
  settings: {
    title: 'Sign In to Customize baba',
    subtitle: 'Save your preferences across devices with a free account.',
    perks: [
      'Sync preferences across devices',
      `${freeChars} characters/month`,
      'No password required',
    ],
  },
}

export function SignInProvider({ children }: { children: React.ReactNode }) {
  const [isOpen, setIsOpen] = useState(false)
  const [intent, setIntent] = useState<SignInIntent>('sidebar')

  const open = useCallback((i: SignInIntent = 'sidebar') => {
    if (isSignedIn()) return
    setIntent(i)
    setIsOpen(true)
  }, [])

  const close = useCallback(() => setIsOpen(false), [])

  // Surfaces rendered above AppShell (the onboarding card) have no access to
  // this context, so they ask through a window event instead. See
  // lib/signInEvents.ts for why that indirection exists.
  useEffect(() => {
    const onRequest = (event: Event) => {
      const detail = (event as CustomEvent<OpenSignInEventDetail>).detail
      const requested = detail?.intent
      open(requested && requested in CONFIGS ? (requested as SignInIntent) : 'sidebar')
    }
    window.addEventListener(OPEN_SIGN_IN_EVENT, onRequest)
    return () => window.removeEventListener(OPEN_SIGN_IN_EVENT, onRequest)
  }, [open])

  useEffect(() => {
    if (!isOpen) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close()
    }
    document.addEventListener('keydown', handler)
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', handler)
      document.body.style.overflow = prev
    }
  }, [isOpen, close])

  return (
    <SignInContext.Provider value={{ open, close }}>
      {children}
      <SignInModal isOpen={isOpen} intent={intent} onClose={close} />
    </SignInContext.Provider>
  )
}

function SignInModal({
  isOpen,
  intent,
  onClose,
}: {
  isOpen: boolean
  intent: SignInIntent
  onClose: () => void
}) {
  const [sendMagicLink] = useSendMagicLinkMutation()
  const [verifyLoginCode] = useVerifyLoginCodeMutation()
  const [email, setEmail] = useState('')
  const [heartbeatOptIn, setHeartbeatOptIn] = useState(true)
  const [loading, setLoading] = useState(false)
  // 'email' = enter address, 'code' = enter the 6-digit code we emailed.
  const [step, setStep] = useState<'email' | 'code'>('email')
  const [code, setCode] = useState('')
  const [verifying, setVerifying] = useState(false)
  const [codeError, setCodeError] = useState<string | null>(null)
  // React state is not synchronous enough to stop two near-simultaneous
  // submits (iOS one-time-code autofill + Enter/click, double onChange, etc.).
  // The backend burns OTPs after one successful claim, so a duplicate request
  // can race the page reload and flash "expired" even though sign-in worked.
  const verifyingRef = useRef(false)

  const [wasExistingUser, setWasExistingUser] = useState(false)
  const [shakeInput, setShakeInput] = useState(false)

  const sent = step === 'code'

  useEffect(() => {
    if (!isOpen) return
    setStep('email')
    setCode('')
    setCodeError(null)
    setLoading(false)
    setVerifying(false)
    const saved = getAuthEmail()
    if (saved) {
      setEmail(saved)
      setWasExistingUser(true)
    } else {
      setWasExistingUser(false)
    }
  }, [isOpen])

  // Adopt a verified session exactly like AppShell does for the link path:
  // store the JWT, seed + broadcast subscription, track, then reload signed in.
  const adoptSession = (result: { token?: string; subscription_status?: string; plan?: string | null; is_new_user?: boolean; eventId?: string }) => {
    if (!result.token) return
    // Owner marker first, then the cookie: another open tab still holding the
    // previous account's chats must already refuse to sync them when this
    // session lands (lib/accountLocalState.ts).
    handAccountLocalStateTo(email.trim())
    setAuthToken(result.token, email.trim())
    const seeded = seedSubscriptionFromAuth({
      subscription_status: result.subscription_status,
      plan: result.plan ?? null,
    })
    broadcastSubscriptionStatus(seeded)
    Sentry.setUser({ email: email.trim() })
    if (result.is_new_user) trackSignUp({ email: email.trim(), eventId: registrationEventId(result.eventId, result.token) })
    else trackLogin({ email: email.trim() })
    if (heartbeatOptIn) enrollHeartbeat(email.trim())
    // Sign-in reloads the page, so the one-time "Use baba on Your Phone" panel
    // cannot be triggered in memory — leave a session marker for the shell to
    // find on the other side (see PhoneLinkPrompt).
    markPhoneLinkPromptPending(email.trim())
    toast.success('Signed in. Welcome to baba!')
    onClose()
    window.location.reload()
  }

  // overrideCode lets auto-submit pass the just-typed/pasted digits directly,
  // instead of reading the `code` state (which hasn't re-rendered yet in that
  // closure — the cause of the "enter the code" error on paste).
  const handleVerifyCode = async (e?: React.FormEvent, overrideCode?: string) => {
    e?.preventDefault()
    if (verifyingRef.current) return
    const digits = (overrideCode ?? code).replace(/\D/g, '')
    if (digits.length !== 6) {
      setCodeError('Enter the 6-digit code from your email.')
      return
    }
    verifyingRef.current = true
    setVerifying(true)
    setCodeError(null)
    try {
      const result = await verifyLoginCode({ email: email.trim(), code: digits }).unwrap()
      if (result.token) {
        adoptSession(result)
      } else {
        setCodeError(result.error || 'That code is incorrect.')
        if (result.remaining === 0) setCode('')
      }
    } catch {
      setCodeError('Could not verify the code. Please try again.')
    } finally {
      verifyingRef.current = false
      setVerifying(false)
    }
  }

  const resendCode = async () => {
    setCode('')
    setCodeError(null)
    setLoading(true)
    try {
      const r = await sendMagicLink({
        email: email.trim(),
        returnUrl: `${window.location.origin}/`,
        referralCode: getStoredReferralCode(),
      }).unwrap()
      if (r.ok) toast.success('New code sent.')
      else toast.error(r.error || 'Could not resend the code.')
    } catch {
      toast.error('Could not resend the code.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (!isOpen || !sent) return
    const started = Date.now()
    const interval = setInterval(() => {
      if (isSignedIn()) {
        clearInterval(interval)
        const signedInEmail = getAuthEmail() ?? undefined
        Sentry.setUser({ email: signedInEmail })
        Sentry.addBreadcrumb({ category: 'auth', message: 'Sign-in completed via magic link', level: 'info' })
        if (wasExistingUser) {
          trackLogin({ email: signedInEmail })
        } else {
          trackSignUp({ email: signedInEmail, eventId: registrationEventId(null, getAuthToken()) })
        }
        if (heartbeatOptIn && signedInEmail) enrollHeartbeat(signedInEmail)
        if (signedInEmail) markPhoneLinkPromptPending(signedInEmail)
        toast.success('Signed in. Welcome to baba!')
        onClose()
        window.location.reload()
      } else if (Date.now() - started > 5 * 60 * 1000) {
        clearInterval(interval)
      }
    }, 3000)
    return () => clearInterval(interval)
  }, [isOpen, sent, onClose, wasExistingUser, heartbeatOptIn])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const trimmed = email.trim()
    if (!trimmed || !/^[^@\s]+@[^@\s]+\.[^@\s]{2,}$/.test(trimmed)) {
      toast.error('Please enter a valid email')
      setShakeInput(true)
      setTimeout(() => setShakeInput(false), 360)
      return
    }
    setLoading(true)
    Sentry.addBreadcrumb({
      category: 'auth',
      message: `Magic link requested (intent: ${intent})`,
      level: 'info',
    })
    trackMagicLinkRequested({ intent })
    try {
      const r = await sendMagicLink({
        email: trimmed,
        returnUrl: `${window.location.origin}/`,
        referralCode: getStoredReferralCode(),
      }).unwrap()
      if (r.ok) {
        setStep('code')
        setCode('')
        setCodeError(null)
        Sentry.addBreadcrumb({ category: 'auth', message: 'Sign-in code sent successfully', level: 'info' })
      } else {
        toast.error(r.error || 'Could not send the code')
        Sentry.captureMessage(`Sign-in code failed: ${r.error ?? 'unknown'}`, 'warning')
      }
    } catch {
      toast.error('Could not send the code')
    } finally {
      setLoading(false)
    }
  }

  const config = CONFIGS[intent] ?? CONFIGS.sidebar
  // A remembered email means this is a returning customer whose session lapsed
  // — possibly an active Pro subscriber. Greet them as such instead of pitching
  // a "free account / upgrade", which reads as "pay again" to someone who has
  // already paid. Their plan unlocks automatically once they sign back in.
  const returning = wasExistingUser
  const heading = returning ? 'Welcome Back' : config.title
  const subheading = returning
    ? 'Your account (and any Pro plan on it) is waiting. Sign in to pick up where you left off.'
    : config.subtitle

  if (typeof document === 'undefined') return null

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.16 }}
          className="fixed inset-0 z-[200] flex items-center justify-center p-4 backdrop-blur-md max-md:items-end max-md:p-0"
          style={{ background: 'rgba(0,0,0,0.55)' }}
          onClick={onClose}
        >
         <FocusLock returnFocus autoFocus>
          <motion.div
            initial={{ opacity: 0, scale: 0.97, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.97, y: 16 }}
            transition={{ duration: 0.18, ease: [0.25, 0.1, 0.25, 1] }}
            // Phone: a bottom sheet rather than a centered dialog.
            className="relative w-full max-w-[420px] rounded-md border border-app bg-surface overflow-hidden shadow-token-lg dialog-max-h flex flex-col max-md:max-w-none max-md:rounded-b-none max-md:rounded-t-[14px] max-md:border-x-0 max-md:border-b-0 max-md:pb-[env(safe-area-inset-bottom)]"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="signin-modal-title"
          >
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="touch-44 absolute top-2.5 right-2.5 md:top-4 md:right-4 h-7 w-7 rounded-md flex items-center justify-center text-subtle hover:text-app hover:bg-[var(--hover)] transition-colors duration-150 z-10 cursor-pointer"
            >
              <X className="h-3.5 w-3.5" strokeWidth={2.5} />
            </button>

            <div className="min-h-0 flex-1 overflow-y-auto px-7 pt-7 pb-7 max-md:px-5 max-md:pt-5 max-md:pb-4">
              <div className="flex flex-col items-center mb-4">
                <div className="flex items-center gap-2 mb-3">
                  <img
                    src="/baba-app-icon.png"
                    alt=""
                    className="h-8 w-8 object-cover"
                    style={{ borderRadius: '8px' }}
                  />
                  <span className="text-[16px] font-semibold text-app">baba</span>
                </div>
                <span
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[10.5px] font-semibold uppercase tracking-wider border"
                  style={{ background: 'var(--card-2)', color: 'var(--muted)', borderColor: 'var(--line)', borderRadius: '4px' }}
                >
                  <Sparkles className="h-3 w-3" strokeWidth={2} />
                  {returning ? 'Welcome Back' : 'Free Account'}
                </span>
              </div>

              <h2 id="signin-modal-title" className="text-center text-[20px] font-semibold tracking-tight leading-tight text-app mb-2">
                {heading}
              </h2>
              <p className="text-center text-[13px] text-muted leading-relaxed mb-6">
                {subheading}
              </p>

              {step === 'email' && (
                <>
                  {!returning && (
                    <ul className="space-y-2 mb-6">
                      {config.perks.map((p) => (
                        <li key={p} className="flex items-center gap-2.5 text-[13px] text-app">
                          <span className="shrink-0 h-5 w-5 rounded flex items-center justify-center" style={{ background: 'var(--card-2)', color: 'var(--muted)' }}>
                            <Check className="h-3 w-3" strokeWidth={3} />
                          </span>
                          <span>{p}</span>
                        </li>
                      ))}
                    </ul>
                  )}

                  <button
                    type="button"
                    onClick={startGoogleSignIn}
                    disabled={loading}
                    className="w-full h-10 rounded-md font-semibold text-app text-[13px] flex items-center justify-center gap-2.5 bg-surface border border-app hover:bg-[var(--hover)] hover:border-[var(--border-strong)] shadow-xs active:scale-[0.98] transition-all disabled:opacity-50 mb-3 cursor-pointer select-none"
                  >
                    <svg width="16" height="16" viewBox="0 0 18 18" aria-hidden="true">
                      <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"/>
                      <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"/>
                      <path fill="#FBBC05" d="M3.964 10.706A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.706V4.962H.957A8.997 8.997 0 0 0 0 9c0 1.452.348 2.827.957 4.038l3.007-2.332z"/>
                      <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.962L3.964 7.294C4.672 5.167 6.656 3.58 9 3.58z"/>
                    </svg>
                    Continue with Google
                  </button>

                  <div className="flex items-center gap-3 mb-3">
                    <div className="flex-1 h-px bg-app/20" />
                    <span className="text-[11px] text-subtle uppercase tracking-wider">or</span>
                    <div className="flex-1 h-px bg-app/20" />
                  </div>
                </>
              )}

              {step === 'email' ? (
                <form onSubmit={handleSubmit} className="space-y-2.5">
                  <input
                    type="email"
                    name="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    autoFocus
                    className={`w-full h-10 px-3.5 rounded-md bg-surface border border-app text-app placeholder:text-subtle focus:outline-none focus:border-[var(--accent)] focus:ring-[3px] focus:ring-[var(--accent-soft)] transition-colors text-[13px] disabled:opacity-60 ${shakeInput ? 'animate-shake border-[var(--err)]' : ''}`}
                  />
                  <label className="flex items-start gap-2 text-[12px] text-muted leading-snug">
                    <input
                      type="checkbox"
                      checked={heartbeatOptIn}
                      onChange={(event) => setHeartbeatOptIn(event.target.checked)}
                      className="mt-0.5"
                    />
                    <span>Also send Tuesday’s Hebrew Heartbeat: one Israeli word and this week’s dates. Unsubscribe anytime.</span>
                  </label>
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full h-11 rounded-md font-semibold text-[14px] transition-all duration-150 hover:brightness-105 active:scale-[0.98] disabled:opacity-50 cursor-pointer shadow-sm hover:shadow-brand-500/20"
                    style={{
                      background: 'var(--accent)',
                      border: '1px solid var(--accent-deep)',
                      color: 'var(--cta-fg)',
                    }}
                  >
                    {loading ? 'Sending…' : 'Email Me a Code'}
                  </button>
                  <p className="text-center text-[11px] text-subtle pt-1">
                    By continuing you agree to our terms & privacy policy.
                  </p>
                  <p className="text-center text-[12.5px] text-subtle pt-1">
                    Pro works on iPhone and Android too. Sign in with the same email.
                  </p>
                </form>
              ) : (
                <form onSubmit={handleVerifyCode} className="space-y-2.5">
                  <p className="text-center text-[13px] text-muted leading-relaxed mb-1">
                    We sent a 6-digit code to <span className="font-semibold text-app">{email}</span>. Enter it below.
                  </p>
                  <input
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    pattern="[0-9]*"
                    maxLength={6}
                    value={code}
                    disabled={verifying}
                    onChange={(e) => {
                      if (verifyingRef.current) return
                      const digits = e.target.value.replace(/\D/g, '').slice(0, 6)
                      setCode(digits)
                      setCodeError(null)
                      if (digits.length === 6) {
                        // Auto-submit on the 6th digit (typing, paste, iOS
                        // autofill). Pass the digits so we don't read stale state.
                        setTimeout(() => handleVerifyCode(undefined, digits), 0)
                      }
                    }}
                    placeholder="123456"
                    autoFocus
                    aria-label="6-digit code"
                    className={`w-full h-14 px-4 rounded-md bg-surface border text-app text-center text-[26px] font-bold tracking-[0.4em] placeholder:tracking-[0.3em] placeholder:text-subtle focus:outline-none transition-colors ${codeError ? 'border-[var(--err)] animate-shake' : 'border-app focus:border-[var(--accent)]'}`}
                  />
                  {codeError && (
                    <p className="text-center text-[12px] text-[var(--err)] font-medium">{codeError}</p>
                  )}
                  <button
                    type="submit"
                    disabled={verifying || code.length !== 6}
                    className="w-full h-11 rounded-md font-semibold text-[14px] transition-opacity hover:opacity-90 active:scale-[0.99] disabled:opacity-50"
                    style={{
                      background: 'var(--accent)',
                      border: '1px solid var(--accent-deep)',
                      color: 'var(--cta-fg)',
                    }}
                  >
                    {verifying ? 'Verifying…' : 'Verify & Sign In'}
                  </button>
                  <div className="flex items-center justify-center gap-4 pt-1 text-[12px]">
                    <button type="button" onClick={resendCode} disabled={loading} className="text-muted hover:text-app transition-colors disabled:opacity-50">
                      {loading ? 'Sending…' : 'Resend code'}
                    </button>
                    <span className="text-subtle" aria-hidden>·</span>
                    <button
                      type="button"
                      onClick={() => { setStep('email'); setCode(''); setCodeError(null) }}
                      className="text-muted hover:text-app transition-colors"
                    >
                      Use a different email
                    </button>
                  </div>
                  <p className="text-center text-[11px] text-subtle pt-1">
                    Code expires in 30 minutes. Not there? Check Spam or Promotions.
                  </p>
                </form>
              )}
            </div>
          </motion.div>
        </FocusLock>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  )
}
